import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Card,
  Flex,
  Input,
  List,
  Modal,
  Space,
  Typography,
} from "antd";
import {
  MailOutlined,
  DeleteOutlined,
  PlusOutlined,
  IdcardOutlined,
} from "@ant-design/icons";
import { ContentTable } from "@authzsvc/common-react-components";
import { validateEmail } from "../../../util";

const MailAliases = ({
  primaryMailAlias,
  mailAliases,
  canModifyGroup,
  updateGroupMailProperties,
}) => {
  const [emailInputError, setEmailInputError] = useState("");
  const [isEmailInputTouched, setIsEmailInputTouched] = useState(false);
  const [emailInputKey, setEmailInputKey] = useState(0);
  const [inputValue, setInputValue] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isRemoving, setIsRemoving] = useState(false);
  const cernEmailDomain = "@cern.ch";
  const hasSelections = selectedRows.length > 0;
  const rowSelection = {
    selectedRowKeys: selectedRows,
    onChange: (selectedRowKeys) => {
      setSelectedRows(selectedRowKeys);
    },
  };
  const tableColumns = [
    { title: "Email Alias", dataIndex: "alias", key: "alias" },
  ];
  const dataSource =
    mailAliases?.map((alias, i) => ({
      key: i,
      alias: alias,
    })) || [];

  const getRowKey = (mailAlias) => {
    return mailAlias?.alias;
  };

  const validateEmailInput = (mailAliasLocal, dataToCompare) => {
    const mailAliasLocalWithDomain = mailAliasLocal + cernEmailDomain;
    if (!mailAliasLocal) {
      return "Email is required";
    } else if (!validateEmail(mailAliasLocalWithDomain)) {
      return "Please enter a valid email";
    } else if (
      dataToCompare?.includes(mailAliasLocalWithDomain) ||
      mailAliasLocalWithDomain === primaryMailAlias
    ) {
      return "This email alias already exists";
    }
    return "";
  };

  const handleEmailInputChange = (e) => {
    setInputValue(e.target.value);
    if (isEmailInputTouched) {
      setEmailInputError(validateEmailInput(e.target.value, mailAliases));
    }
  };

  const handleEmailInputBlur = (e) => {
    setIsEmailInputTouched(true);
    setEmailInputError(validateEmailInput(e.target.value, mailAliases));
  };

  const handleAdd = () => {
    setIsEmailInputTouched(false);
    setEmailInputKey((prevKey) => prevKey + 1);
    setInputValue("");
    setEmailInputError("");
    setIsAddModalOpen(true);
  };

  const handleAddModalOk = () => {
    if (!validateEmailInput(inputValue)) {
      const updatedMailAliases = [...mailAliases, inputValue + cernEmailDomain];
      updateGroupMailProperties({ mailAliases: updatedMailAliases });
      setIsAddModalOpen(false);
    }
  };

  const handleAddModalCancel = () => {
    setIsAddModalOpen(false);
  };

  const handleRemove = () => {
    setIsRemoveModalOpen(true);
  };

  const handleRemoveModalOk = () => {
    setIsRemoving(true);
    const updatedMailAliases = mailAliases?.filter(
      (alias) => !selectedRows.includes(alias)
    );
    updateGroupMailProperties({ mailAliases: updatedMailAliases });
    setSelectedRows([]);
    rowSelection.selectedRows = [];
    setIsRemoving(false);
    setIsRemoveModalOpen(false);
  };

  const handleRemoveModalCancel = () => {
    setIsRemoveModalOpen(false);
  };

  return (
    <>
      <Card
        style={{ marginBottom: 14 }}
        title={
          <Typography.Title level={4} style={{ margin: 0 }}>
            <IdcardOutlined style={{ marginRight: 8, color: "#bfbfbf" }} />
            Email aliases
          </Typography.Title>
        }
      >
        <Space size="middle" style={{ marginBottom: 16 }}>
          <Typography.Text type="secondary">
            <MailOutlined style={{ marginRight: 8 }} />
            Primary Email Alias:
          </Typography.Text>
          <Typography.Text style={{ fontSize: "16px" }}>
            {primaryMailAlias}
          </Typography.Text>
        </Space>
        <Flex justify="end" style={{ marginBottom: 24 }}>
          <Button
            icon={<PlusOutlined />}
            disabled={!canModifyGroup}
            color="green"
            variant="solid"
            id="mailAliasesAddButton"
            onClick={handleAdd}
          >
            Add Mail Alias
          </Button>
        </Flex>
        <ContentTable
          rowKey={getRowKey}
          columns={tableColumns}
          dataSource={dataSource}
          rowSelection={rowSelection}
        />
        {dataSource.length > 0 && (
          <div style={{ marginTop: "-48px" }}>
            <Button
              id="mailAliasesRemoveSelectionButton"
              loading={isRemoving}
              disabled={!hasSelections || !canModifyGroup}
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemove}
            />
          </div>
        )}
      </Card>
      <Modal
        title="Add Email Alias"
        open={isAddModalOpen}
        centered
        onCancel={handleAddModalCancel}
        onOk={handleAddModalOk}
        okButtonProps={{
          disabled: !!emailInputError,
          color: "green",
          variant: "solid",
        }}
      >
        <Input
          key={emailInputKey}
          autoFocus
          prefix={<MailOutlined />}
          addonAfter={cernEmailDomain}
          placeholder="Enter email alias to be added"
          value={inputValue}
          onChange={handleEmailInputChange}
          onBlur={handleEmailInputBlur}
          status={emailInputError ? "error" : ""}
        />
        {emailInputError && (
          <span
            style={{
              color: "#ff4d4f",
              marginTop: "4px",
              marginLeft: "4px",
              display: "block",
            }}
          >
            {emailInputError}
          </span>
        )}
      </Modal>
      <Modal
        title={
          <span>
            <DeleteOutlined style={{ marginRight: 8, color: "#ff4d4f" }} />
            Confirm Deletion
          </span>
        }
        open={isRemoveModalOpen}
        centered
        onCancel={handleRemoveModalCancel}
        onOk={handleRemoveModalOk}
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to remove the following email aliases?</p>
        <List
          dataSource={[...selectedRows]}
          renderItem={(item) => <List.Item>{item}</List.Item>}
          bordered
          size="small"
        />
      </Modal>
    </>
  );
};

MailAliases.propTypes = {
  primaryMailAlias: PropTypes.string,
  mailAliases: PropTypes.arrayOf(PropTypes.string),
  canModifyGroup: PropTypes.bool,
  updateGroupMailProperties: PropTypes.func,
};

export default MailAliases;
